import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

const ReportCharts = ({ reportType, reportData }) => {
  // Helper function to process investment data for the pie chart based on status
  const processInvestmentData = (data) => {
    if (!data || !data.data) return [];
    
    const statusCounts = {
      'Approved': 0,
      'Pending': 0,
      'Rejected': 0,
      'Not Submitted': 0
    };

    data.data.forEach(item => {
      const status = item.status || 'Not Submitted';
      if (statusCounts.hasOwnProperty(status)) {
        statusCounts[status]++;
      } else {
        statusCounts['Not Submitted']++;
      }
    });

    return Object.entries(statusCounts)
      .filter(([name, value]) => value > 0) // Only include categories with data
      .map(([name, value]) => ({
        name,
        value
      }));
  };

  // Helper function to process HRA proof status for the chart
  const processHraData = (data) => {
    if (!data || !data.data) return [];
    
    const statusCounts = {
      'approved': 0,
      'pending': 0,
      'rejected': 0,
      'Not Submitted': 0
    };

    data.data.forEach(item => {
      // Check various possible status field names
      const status = item.status || item.proofStatus || item.purpose || 'Not Submitted';
      if (statusCounts.hasOwnProperty(status)) {
        statusCounts[status]++;
      } else {
        statusCounts['Not Submitted']++;
      }
    });

    return Object.entries(statusCounts)
      .filter(([name, value]) => value > 0) // Only include categories with data
      .map(([name, value]) => ({
        name,
        value
      }));
  };

  // Helper function to process tax summary data based on better regime
  const processTaxSummaryData = (data) => {
    if (!data || !data.data) return [];
    
    const regimeCounts = {
      'Old Regime': 0,
      'New Regime': 0,
      'No Preference': 0
    };

    data.data.forEach(item => {
      const betterRegime = item.better_regime || item.betterRegime || 'No Preference';
      if (regimeCounts.hasOwnProperty(betterRegime)) {
        regimeCounts[betterRegime]++;
      } else {
        regimeCounts['No Preference']++;
      }
    });

    return Object.entries(regimeCounts)
      .filter(([name, value]) => value > 0) // Only include categories with data
      .map(([name, value]) => ({
        name,
        value
      }));
  };

  // Updated helper for payroll summary focusing on monthly tax data
  const processPayrollData = (data) => {
    if (!data || !data.data || !Array.isArray(data.data)) return [];

    // Count employees with 0 tax vs more than 0 tax
    const taxCounts = {
      'Zero Tax': 0,
      'Tax Applicable': 0
    };

    data.data.forEach((record) => {
      const monthlyTax = record.monthly_tax || 0;
      if (monthlyTax === 0) {
        taxCounts['Zero Tax']++;
      } else {
        taxCounts['Tax Applicable']++;
      }
    });

    return Object.entries(taxCounts).map(([name, value]) => ({
      name,
      value
    }));
  };

  // Alternative approach: Monthly tax trends (if you have monthly data)
  const processMonthlyTaxTrends = (data) => {
    if (!data || !data.data || !Array.isArray(data.data)) return [];

    // Group by month and sum tax amounts
    const monthlyTax = {};
    
    data.data.forEach((record) => {
      const month = record.month || 'Unknown';
      if (!monthlyTax[month]) {
        monthlyTax[month] = {
          name: month,
          totalTax: 0,
          incomeTax: 0,
          professionalTax: 0,
          otherTax: 0
        };
      }
      
      const incomeTax = record.income_tax || record.tds || 0;
      const professionalTax = record.professional_tax || record.pt || 0;
      const otherTax = (record.esi_deduction || 0) + (record.pf_deduction || 0) + (record.other_tax_deductions || 0);
      
      monthlyTax[month].incomeTax += incomeTax;
      monthlyTax[month].professionalTax += professionalTax;
      monthlyTax[month].otherTax += otherTax;
      monthlyTax[month].totalTax += incomeTax + professionalTax + otherTax;
    });

    return Object.values(monthlyTax);
  };

  const processEmployeeTaxData = (data) => {
    if (!data || !data.taxData) return [];
    
    return [
      { name: 'Old Regime', value: data.taxData.initial?.total_tax_payable_old_regime || 0 },
      { name: 'New Regime', value: data.taxData.initial?.total_tax_payable_new_regime || 0 }
    ];
  };

  const getChartData = () => {
    if (!reportData) return null;

    try {
      switch (reportType) {
        case 'investment':
          return { type: 'pie', data: processInvestmentData(reportData) };
        case 'hra':
          return { type: 'pie', data: processHraData(reportData) };
        case 'tax-summary':
          return { type: 'pie', data: processTaxSummaryData(reportData) };
        case 'payroll-summary':
          // Show employee count based on monthly tax (0 vs >0)
          return { type: 'pie', data: processPayrollData(reportData) };
        case 'employee-tax':
          return { type: 'bar', data: processEmployeeTaxData(reportData) };
        case 'cost-center-summary':
          return {
            type: 'bar',
            data: (reportData.data || []).map(item => ({
              name: `${item.name} (${item.code})`,
              value: item.totalNetSalary
            }))
          };
        default:
          return null;
      }
    } catch (error) {
      //console.error('Error processing chart data:', error);
      return null;
    }
  };

  const getChartTitle = () => {
    switch (reportType) {
      case 'investment':
        return 'Investment Declaration Status Distribution';
      case 'hra':
        return 'Allowance Claims Status Distribution';
      case 'tax-summary':
        return 'Better Tax Regime Distribution';
      case 'payroll-summary':
        return 'Employee Tax Distribution';
      case 'employee-tax':
        return 'Tax Comparison: Old vs New Regime';
      case 'cost-center-summary':
        return 'Salary Expenditure by Cost Center (INR)';
      default:
        return reportType.replace(/-/g, ' ').toUpperCase() + ' Visualization';
    }
  };

  const chartData = getChartData();

  if (!chartData || !chartData.data || chartData.data.length === 0) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography>No chart data available for this report.</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        {getChartTitle()}
      </Typography>
      
      <ResponsiveContainer width="100%" height={400}>
        {chartData.type === 'bar' ? (
          chartData.isMultiSeries ? (
            // Multi-series bar chart for monthly tax trends
            <BarChart data={chartData.data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="incomeTax" fill="#0088FE" name="Income Tax" />
              <Bar dataKey="professionalTax" fill="#00C49F" name="Professional Tax" />
              <Bar dataKey="otherTax" fill="#FFBB28" name="Other Tax" />
            </BarChart>
          ) : (
            // Single series bar chart
            <BarChart data={chartData.data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="value" fill="#8884d8" />
            </BarChart>
          )
        ) : (
          <PieChart>
            <Pie
              data={chartData.data}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={120}
              fill="#8884d8"
              dataKey="value"
              label={({ name, percent, value }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
            >
              {chartData.data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value, name) => [value, name]} />
            <Legend />
          </PieChart>
        )}
      </ResponsiveContainer>
    </Paper>
  );
};

export default ReportCharts;